import { ALL_SINS, type CarnivalGameState, type CarnivalPlayer } from "./types";
import {
  createRngState,
  nextBounded,
  revealOrderFrom,
  rollAllDice,
} from "./utils";

export interface InitGameOptions {
  readonly gameId?: string;
  readonly seed: string;
  readonly players: readonly CarnivalPlayer[];
  /** Defaults to a seed-derived random seat (the game has no in-person "confession" rule digitally). */
  readonly devilCardHolderId?: string;
}

const MIN_PLAYERS = 2;
const MAX_PLAYERS = 5;

export function initGame(options: InitGameOptions): CarnivalGameState {
  if (
    options.players.length < MIN_PLAYERS ||
    options.players.length > MAX_PLAYERS
  ) {
    throw new Error(
      `Carnival of Sins requires ${MIN_PLAYERS}-${MAX_PLAYERS} players`,
    );
  }
  const playerIds = options.players.map((player) => player.playerId);

  let rngState = createRngState(options.seed);
  let devilCardHolderId = options.devilCardHolderId;
  if (devilCardHolderId === undefined) {
    const draw = nextBounded(rngState, playerIds.length);
    rngState = draw.rngState;
    devilCardHolderId = playerIds[Math.floor(draw.value)];
  } else if (!playerIds.includes(devilCardHolderId)) {
    throw new Error("devilCardHolderId must be one of the players");
  }

  const roll = rollAllDice(rngState);

  return {
    gameId: options.gameId ?? `carnival-of-sins-${playerIds.join("-")}`,
    gameSeed: options.seed,
    rngState: roll.rngState,
    phase: "SECRET_SELECT",
    round: 1,
    players: options.players,
    seatingOrder: playerIds,
    devilCardHolderId,
    revealOrder: revealOrderFrom(playerIds, devilCardHolderId),
    nextToRevealIndex: 0,
    dice: roll.dice,
    handsByPlayerId: Object.fromEntries(
      playerIds.map((id) => [id, [...ALL_SINS]]),
    ),
    selectionsByPlayerId: Object.fromEntries(playerIds.map((id) => [id, null])),
    revealedThisRound: [],
    scoreHistoryByPlayerId: Object.fromEntries(playerIds.map((id) => [id, []])),
    lastRoundSummary: null,
    winnerIds: null,
  };
}
